import React, { ReactElement, ReactNode, useEffect } from 'react';

import { useAuth } from '@opendoor/auth-fe';
import { Box } from '@opendoor/bricks-next';
import { useRouter } from 'next/router';

import type { NavHeaderData } from 'cms/withNavHeader';

import Header from 'components/landing-pages-v2/shared/header/Header';

interface BuyerDashboardLayoutProps {
  children: ReactNode;
  navHeader?: NavHeaderData | null;
}

export const BuyerDashboardLayout: React.FC<BuyerDashboardLayoutProps> = ({
  children,
  navHeader,
}) => {
  const { authentication } = useAuth();
  const router = useRouter();

  // Handle auth redirect at layout level
  useEffect(() => {
    if (authentication.state === 'not_authenticated') {
      window.location.assign(`/login?redirectTo=${encodeURIComponent(router.asPath)}`);
    }
  }, [authentication.state, router.asPath]);

  // Don't render protected content while redirecting
  if (authentication.state === 'not_authenticated') {
    return null;
  }

  return (
    <Box
      flexDirection="column"
      backgroundColor="$backgroundPrimary"
      style={{ overflowX: 'hidden' }}
    >
      <Box
        display="block"
        width="100%"
        zIndex={2}
        style={{ position: 'fixed', boxShadow: '0px 2px 4px 0px rgba(0, 0, 0, 0.12)' }}
      >
        <Header
          slug="dashboard"
          mainNavLinks={navHeader?.mainNavLinks ?? []}
          menuPanelNavLinks={navHeader?.menuPanelNavLinks ?? []}
          megaNavData={navHeader?.megaNavData}
          ctaTitle={navHeader?.ctaTitle}
          ctaLink={navHeader?.ctaLink}
        />
      </Box>
      <Box
        tag="main"
        minHeight="100vh"
        flexDirection="column"
        backgroundColor="$backgroundPrimary"
        width="100%"
        paddingTop={124}
      >
        {children}
      </Box>
    </Box>
  );
};

// Shared getLayout function to ensure React recognizes it's the same layout across routes
export const getBuyerDashboardLayout = (page: ReactElement) => {
  return <BuyerDashboardLayout navHeader={page.props.navHeader}>{page}</BuyerDashboardLayout>;
};
